// Fixed useFilterAutocomplete.ts - Proper integration between parser and suggestion engine

import { MutableRefObject, useCallback, useMemo, useState } from 'react';
import { FilterParser } from './FilterParser';

import { Suggestion } from './types';
import type { FilterCriteria } from './vocabulary';
import {
  InsertionResult,
  insertSuggestionWithParser,
} from './autocompleteUtils';
import { ParseContext, ParseResult } from './FilterAST';
import { EnhancedSuggestionEngine } from './SuggestionEngine';

interface UseFilterAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  filterOptions: Record<string, FilterCriteria>;
  shapeData?: any[];
  textAreaRef: MutableRefObject<HTMLTextAreaElement | null>;
}

type LegacyToken = {
  type: 'field' | 'operator' | 'value' | 'logical';
  value: string;
  raw: string;
  start: number;
  end: number;
};

interface UseFilterAutocompleteReturn {
  // State
  isAutocompleteOpen: boolean;
  cursorPosition: number;
  focusedSuggestionIndex: number;

  // Derived data
  suggestions: Suggestion[];
  parseResult: ParseResult;
  context: ParseContext | null;
  currentToken: LegacyToken | null;
  expectedNext: string; // Legacy compatibility

  // Actions
  setIsAutocompleteOpen: (open: boolean) => void;
  setCursorPosition: (position: number) => void;
  setFocusedSuggestionIndex: (index: number) => void;
  handleSelectSuggestion: (suggestion: Suggestion) => void;
  handleChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  handleSelect: (e: React.SyntheticEvent<HTMLTextAreaElement>) => void;
  handleFocus: () => void;
  handleBlur: () => void;
}

export function useFilterAutocomplete({
  value,
  onChange,
  filterOptions,
  shapeData,
  textAreaRef,
}: UseFilterAutocompleteProps): UseFilterAutocompleteReturn {
  const [isAutocompleteOpen, setIsAutocompleteOpen] = useState(false);
  const [cursorPosition, setCursorPosition] = useState(0);
  const [focusedSuggestionIndex, setFocusedSuggestionIndex] = useState(0);
  const [isTextareaFocused, setIsTextareaFocused] = useState(false);

  // Create parser and suggestion engine
  const parser = useMemo(() => {
    return new FilterParser(filterOptions);
  }, [filterOptions]);

  const suggestionEngine = useMemo(() => {
    return new EnhancedSuggestionEngine(filterOptions, shapeData);
  }, [filterOptions, shapeData]);

  // Parse and get suggestions - THE KEY FIX
  const { parseResult, suggestions, context } = useMemo(() => {
    // Don't try to parse if filterOptions is empty
    if (!filterOptions || Object.keys(filterOptions).length === 0) {
      return {
        parseResult: {
          ast: {
            type: 'Filter',
            position: { start: 0, end: 0 },
            expressions: [],
            logicalOperators: [],
          },
          isValid: false,
          errors: [],
          context: null,
          incompleteNodes: [],
        } as ParseResult,
        suggestions: [],
        context: null,
      };
    }

    const parseResult = parser.parse(value, cursorPosition);
    const suggestions = suggestionEngine.getSuggestions(
      parseResult,
      '', // Let the engine determine the search term
      value,
      cursorPosition,
    );

    return {
      parseResult,
      suggestions,
      context: parseResult.context,
    };
  }, [value, cursorPosition, parser, suggestionEngine, filterOptions]); // KEY: Added filterOptions to dependencies

  // Handle suggestion selection
  const handleSelectSuggestion = useCallback(
    (suggestion: Suggestion) => {
      if (suggestion.isHint) {
        return;
      }

      // Use the new parser-based insertion
      const result: InsertionResult = insertSuggestionWithParser(
        suggestion,
        value,
        cursorPosition,
        parseResult,
      );

      onChange(result.newValue);

      // Focus and position cursor
      setTimeout(() => {
        if (textAreaRef.current) {
          textAreaRef.current.focus();
          textAreaRef.current.setSelectionRange(
            result.newCursorPosition,
            result.newCursorPosition,
          );
          setCursorPosition(result.newCursorPosition);

          // CRITICAL: Re-analyze context after insertion
          if (result.shouldKeepOpen) {
            setTimeout(() => {
              // Parse the new state
              const updatedParseResult = parser.parse(
                result.newValue,
                result.newCursorPosition,
              );

              // Get new suggestions based on updated context
              const newSuggestions = suggestionEngine.getSuggestions(
                updatedParseResult,
                '',
                result.newValue,
                result.newCursorPosition,
              );

              if (newSuggestions.length > 0) {
                setIsAutocompleteOpen(true);
                setFocusedSuggestionIndex(0);
              } else {
                setIsAutocompleteOpen(false);
              }
            }, 50); // Slightly longer delay to ensure state updates
          } else {
            setIsAutocompleteOpen(false);
          }
        }
      }, 0);
    },
    [
      value,
      cursorPosition,
      onChange,
      textAreaRef,
      parser,
      suggestionEngine,
      parseResult,
    ],
  );

  // Handle text change
  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const newValue = e.target.value;
      const newCursorPosition = e.target.selectionStart || 0;

      onChange(newValue);
      setCursorPosition(newCursorPosition);

      // Open suggestions when typing - but only if we have suggestions
      // The suggestions will be recalculated automatically due to the useMemo dependency
    },
    [onChange],
  );

  // Handle cursor position change
  const handleSelect = useCallback(
    (e: React.SyntheticEvent<HTMLTextAreaElement>) => {
      const newCursorPosition =
        (e.target as HTMLTextAreaElement).selectionStart || 0;
      setCursorPosition(newCursorPosition);
    },
    [],
  );

  // Handle focus and blur
  const handleFocus = useCallback(() => {
    setIsTextareaFocused(true);
  }, []);

  const handleBlur = useCallback(() => {
    setIsTextareaFocused(false);
  }, []);

  // Only auto-open suggestions when textarea is focused AND we have suggestions
  useMemo(() => {
    // Only show suggestions if the textarea is actually focused
    if (isTextareaFocused && suggestions.length > 0 && value.length > 0) {
      setIsAutocompleteOpen(true);
    } else {
      setIsAutocompleteOpen(false);
    }
  }, [suggestions.length, value.length, isTextareaFocused]);

  // Derive legacy compatibility values from parse context
  const currentToken = useMemo(() => {
    if (!context?.currentNode) return null;

    const node = context.currentNode;
    switch (node.type) {
      case 'Field':
        return {
          type: 'field' as const,
          value: node.name,
          raw: node.raw,
          start: node.position.start,
          end: node.position.end,
        };
      case 'Operator':
        return {
          type: 'operator' as const,
          value: node.operator,
          raw: node.raw,
          start: node.position.start,
          end: node.position.end,
        };
      case 'Value':
        return {
          type: 'value' as const,
          value: node.value,
          raw: node.raw,
          start: node.position.start,
          end: node.position.end,
        };
      case 'LogicalOperator':
        return {
          type: 'logical' as const,
          value: node.operator,
          raw: node.raw,
          start: node.position.start,
          end: node.position.end,
        };
      default:
        return null;
    }
  }, [context]);

  const expectedNext = useMemo(() => {
    return context?.expectsNext || 'Field';
  }, [context]);

  return {
    // State
    isAutocompleteOpen,
    cursorPosition,
    focusedSuggestionIndex,

    // Derived data
    suggestions,
    parseResult,
    context,
    currentToken,
    expectedNext,

    // Actions
    setIsAutocompleteOpen,
    setCursorPosition,
    setFocusedSuggestionIndex,
    handleSelectSuggestion,
    handleChange,
    handleSelect,
    handleFocus,
    handleBlur,
  };
}
